// Route generation and validation logic for Model Town Board
// KENYAN ROUNDABOUT RULES - STRICT IMPLEMENTATION

/**
 * ROUNDABOUT LANE & TURN RULES (Model Town Board – Kenya, Central Green Island)
 *
 * 1. Roundabout Structure:
 *    - Central green island (non-drivable, cannot be crossed)
 *    - Clockwise movement only around the island
 *    - 4 circulating lanes concentric to green island
 *
 * 2. Lane Rules:
 *    - Each approach lane maps directly to a circulating lane
 *    - Four-lane approach → circulating lanes 1–4
 *    - Three-lane approach → circulating lanes 1–3
 *    - Lane alignment on entry is NOT a lane change
 *    - Once aligned, lane is locked until exit
 *    - NO lane changes inside the roundabout
 *
 * 3. Turn Types (Precise Geometric Definition):
 *    - Left Turn → first exit after entry road
 *    - Straight → exit approximately opposite entry
 *    - Right Turn → exit one before entry road in clockwise order
 *    - U-Turn → return to same entry road (smallest clockwise path, using innermost lane)
 *    - Left 270° Turn → exit three roads after entry (clockwise)
 *    - Full Circle → complete full loop, return to same entry without stopping
 *
 * 4. Lane Turn Permissions:
 *
 *    FOUR-LANE APPROACH:
 *    - Lane 1 (outermost): Left, Straight
 *    - Lane 2: Straight
 *    - Lane 3: Straight, Right
 *    - Lane 4 (innermost): Right, U-Turn, Left 270°, Full Circle
 *
 *    THREE-LANE APPROACH:
 *    - Lane 1 (outermost): Left, Straight
 *    - Lane 2: Straight
 *    - Lane 3 (innermost): Straight, Right, U-Turn
 *
 * 5. Invalid Actions (ALWAYS DISALLOWED):
 *    - Crossing or touching the green island
 *    - Lane changes inside the roundabout
 *    - U-Turn from non-permitted lanes
 *    - Counter-clockwise movement
 *    - Stopping inside the roundabout
 *    - Overtaking inside the roundabout
 */

// Roundabout Constants
const ROUNDABOUT_CENTER = { x: 214, y: 212 };
const ROUNDABOUT_OUTER_RADIUS = 100; // Outer edge of circulating lanes
const GREEN_ISLAND_RADIUS = 20; // Central green island
const LANE_WIDTH = 20; // Each circulating lane is 20px wide

// Define circulating lane radii (from center)
const CIRCULATING_LANE_RADII = {
  1: { min: 84, max: 100 },   // Lane 1 (outermost circulating lane)
  2: { min: 66, max: 84 },    // Lane 2
  3: { min: 48, max: 66 },    // Lane 3
  4: { min: 30, max: 48 }     // Lane 4 (innermost circulating lane, closest to green island)
};

// Entry directions
const ENTRY_DIRECTIONS = {
  NORTH: 'north',
  SOUTH: 'south',
  EAST: 'east',
  WEST: 'west'
};

// Turn types
const TURN_TYPES = {
  LEFT: 'left',           // 1st exit (90° clockwise)
  STRAIGHT: 'straight',   // 2nd exit (180° clockwise)
  RIGHT: 'right',         // 3rd exit (270° clockwise)
  UTURN: 'uturn',         // 4th exit (360° clockwise, back to entry)
  LEFT_270: 'left_270',   // 3 exits after entry (270° turn)
  FULL_CIRCLE: 'full_circle' // Complete 360° and return to entry
};

/**
 * STRICT ROUNDABOUT SCENARIOS
 * 10 games testing different entry lanes, turn types, and rule compliance
 */
const ROUNDABOUT_SCENARIOS = [
  // SCENARIO 1: West Entry, Lane 1 → Left Turn
  {
    id: 'rb_west_lane1_left',
    name: 'West Entry - Lane 1 Left Turn',
    description: 'Enter from west in Lane 1 (outermost), take first exit (left turn)',
    entryDirection: 'west',
    entryLane: 1,
    expectedTurn: 'left',
    expectedExit: 'north',
    start: { x: 30, y: 150, angle: 0 }, // West road, Lane 1 (outermost)
    checkpoints: [
      { x: 100, y: 150, type: 'approach', rule: 'Approach roundabout in Lane 1 (outermost)' },
      { x: 130, y: 150, type: 'entry_prepare', rule: 'Prepare to enter roundabout - stay in Lane 1' },
      { x: 150, y: 170, type: 'enter', rule: 'Enter roundabout, align to circulating Lane 1' },
      { x: 170, y: 190, type: 'circulate', rule: 'Circulate in Lane 1 (outermost), no lane changes' },
      { x: 190, y: 200, type: 'prepare_exit', rule: 'Prepare to exit left (first exit)' },
      { x: 200, y: 180, type: 'exit', rule: 'Exit to north road in Lane 1' },
      { x: 200, y: 140, type: 'complete', rule: 'Left turn completed - Lane 1 to Exit 1' }
    ],
    difficulty: 'easy',
    strictRules: {
      allowedTurns: ['left', 'straight'],
      forbiddenTurns: ['right', 'uturn', 'left_270', 'full_circle'],
      mustStayInLane: 1,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 2: West Entry, Lane 2 → Straight Through
  {
    id: 'rb_west_lane2_straight',
    name: 'West Entry - Lane 2 Straight',
    description: 'Enter from west in Lane 2, go straight (second exit)',
    entryDirection: 'west',
    entryLane: 2,
    expectedTurn: 'straight',
    expectedExit: 'east',
    start: { x: 30, y: 170, angle: 0 }, // West road, Lane 2
    checkpoints: [
      { x: 100, y: 170, type: 'approach', rule: 'Approach roundabout in Lane 2' },
      { x: 135, y: 175, type: 'enter', rule: 'Enter roundabout, align to circulating Lane 2' },
      { x: 160, y: 190, type: 'circulate_1', rule: 'Circulate clockwise in Lane 2 only' },
      { x: 190, y: 212, type: 'circulate_2', rule: 'Continue circulating - do NOT exit at first exit' },
      { x: 220, y: 225, type: 'prepare_exit', rule: 'Approach second exit (straight/east)' },
      { x: 250, y: 235, type: 'exit', rule: 'Exit to east road' },
      { x: 290, y: 243, type: 'complete', rule: 'Straight through completed - Lane 2 to Exit 2' }
    ],
    difficulty: 'medium',
    strictRules: {
      allowedTurns: ['straight'],
      forbiddenTurns: ['left', 'right', 'uturn', 'left_270', 'full_circle'],
      mustStayInLane: 2,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 3: West Entry, Lane 3 → Right Turn
  {
    id: 'rb_west_lane3_right',
    name: 'West Entry - Lane 3 Right Turn',
    description: 'Enter from west in Lane 3, take third exit (right turn)',
    entryDirection: 'west',
    entryLane: 3,
    expectedTurn: 'right',
    expectedExit: 'south',
    start: { x: 30, y: 190, angle: 0 }, // West road, Lane 3
    checkpoints: [
      { x: 100, y: 190, type: 'approach', rule: 'Approach roundabout in Lane 3' },
      { x: 140, y: 200, type: 'enter', rule: 'Enter roundabout, align to circulating Lane 3' },
      { x: 160, y: 210, type: 'circulate_1', rule: 'Circulate clockwise in Lane 3 around green island' },
      { x: 175, y: 205, type: 'circulate_2', rule: 'Continue around island - pass Exit 1 (north)' },
      { x: 190, y: 190, type: 'circulate_3', rule: 'Keep circulating around green island' },
      { x: 205, y: 175, type: 'circulate_4', rule: 'Top of island - continue around (Lane 3)' },
      { x: 223, y: 175, type: 'circulate_5', rule: 'Pass top, continue circulating' },
      { x: 238, y: 190, type: 'circulate_6', rule: 'Pass Exit 2 (east) - stay in Lane 3' },
      { x: 253, y: 205, type: 'circulate_7', rule: 'Continue around island toward Exit 3' },
      { x: 260, y: 220, type: 'circulate_8', rule: 'Approaching Exit 3 (south) - prepare to exit' },
      { x: 253, y: 235, type: 'prepare_exit', rule: 'Prepare to exit to south road (270° turn)' },
      { x: 238, y: 250, type: 'exit', rule: 'Exit to south road in Lane 3' },
      { x: 220, y: 270, type: 'exit_complete', rule: 'Exiting roundabout to south' },
      { x: 210, y: 300, type: 'complete', rule: 'Right turn completed - traveled 270° around island' }
    ],
    difficulty: 'medium',
    strictRules: {
      allowedTurns: ['straight', 'right'],
      forbiddenTurns: ['left', 'uturn', 'left_270', 'full_circle'],
      mustStayInLane: 3,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 4: West Entry, Lane 4 → U-Turn
  {
    id: 'rb_west_lane4_uturn',
    name: 'West Entry - Lane 4 U-Turn',
    description: 'Enter from west in Lane 4 (innermost), perform U-turn around green island',
    entryDirection: 'west',
    entryLane: 4,
    expectedTurn: 'uturn',
    expectedExit: 'west',
    start: { x: 30, y: 210, angle: 0 }, // West road, Lane 4 (innermost)
    checkpoints: [
      { x: 100, y: 210, type: 'approach', rule: 'Approach roundabout in Lane 4 (innermost)' },
      { x: 125, y: 212, type: 'enter', rule: 'Enter roundabout, align to circulating Lane 4' },
      { x: 150, y: 212, type: 'circulate_1', rule: 'Circulate clockwise around green island (Lane 4)' },
      { x: 175, y: 200, type: 'circulate_2', rule: 'Continue around island - pass Exit 1 (north)' },
      { x: 195, y: 180, type: 'circulate_3', rule: 'Keep circulating - traveling around green island' },
      { x: 214, y: 165, type: 'circulate_4', rule: 'Top of island - continue around' },
      { x: 233, y: 180, type: 'circulate_5', rule: 'Pass Exit 2 (east) - stay in Lane 4' },
      { x: 253, y: 200, type: 'circulate_6', rule: 'Continue circulating around island' },
      { x: 265, y: 212, type: 'circulate_7', rule: 'Pass Exit 3 (south) - stay in Lane 4' },
      { x: 253, y: 224, type: 'circulate_8', rule: 'Complete circle around green island' },
      { x: 233, y: 244, type: 'circulate_9', rule: 'Approaching Exit 4 (back to west)' },
      { x: 214, y: 259, type: 'prepare_exit', rule: 'Prepare to exit back to west road (U-turn)' },
      { x: 195, y: 244, type: 'exit', rule: 'Exit back to west road (Lane 4)' },
      { x: 175, y: 224, type: 'exit_complete', rule: 'Exiting roundabout' },
      { x: 130, y: 210, type: 'complete', rule: 'U-turn completed - traveled 360° around island' }
    ],
    difficulty: 'hard',
    strictRules: {
      allowedTurns: ['right', 'uturn', 'left_270', 'full_circle'],
      forbiddenTurns: ['left', 'straight'],
      mustStayInLane: 4,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 5: North Entry, Lane 1 → Left Turn
  {
    id: 'rb_north_lane1_left',
    name: 'North Entry - Lane 1 Left Turn',
    description: 'Enter from north in Lane 1 (outermost), take first exit (left turn to west)',
    entryDirection: 'north',
    entryLane: 1,
    expectedTurn: 'left',
    expectedExit: 'west',
    start: { x: 170, y: 30, angle: Math.PI / 2 }, // North road, Lane 1
    checkpoints: [
      { x: 170, y: 80, type: 'approach', rule: 'Approach roundabout in Lane 1 (outermost)' },
      { x: 170, y: 120, type: 'entry_prepare', rule: 'Prepare to enter - stay in Lane 1' },
      { x: 175, y: 140, type: 'enter', rule: 'Enter roundabout in circulating Lane 1' },
      { x: 185, y: 160, type: 'circulate', rule: 'Circulate in Lane 1 (outermost)' },
      { x: 195, y: 180, type: 'prepare_exit', rule: 'Prepare to exit left (first exit)' },
      { x: 185, y: 200, type: 'exit', rule: 'Exit to west road' },
      { x: 160, y: 210, type: 'complete', rule: 'Left turn completed successfully' }
    ],
    difficulty: 'easy',
    strictRules: {
      allowedTurns: ['left', 'straight'],
      forbiddenTurns: ['right', 'uturn'],
      mustStayInLane: 1,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 6: North Entry, Lane 2 → Straight Through
  {
    id: 'rb_north_lane2_straight',
    name: 'North Entry - Lane 2 Straight',
    description: 'Enter from north in Lane 2, go straight through to south',
    entryDirection: 'north',
    entryLane: 2,
    expectedTurn: 'straight',
    expectedExit: 'south',
    start: { x: 190, y: 30, angle: Math.PI / 2 }, // North road, Lane 2
    checkpoints: [
      { x: 190, y: 90, type: 'approach', rule: 'Approach roundabout in Lane 2' },
      { x: 190, y: 125, type: 'enter', rule: 'Enter roundabout in circulating Lane 2' },
      { x: 195, y: 150, type: 'circulate_1', rule: 'Circulate in Lane 2 - pass first exit' },
      { x: 210, y: 180, type: 'circulate_2', rule: 'Continue circulating in Lane 2' },
      { x: 220, y: 210, type: 'circulate_3', rule: 'Approach second exit (straight/south)' },
      { x: 225, y: 240, type: 'prepare_exit', rule: 'Prepare to exit straight' },
      { x: 235, y: 270, type: 'exit', rule: 'Exit to south road' },
      { x: 240, y: 310, type: 'complete', rule: 'Straight through completed' }
    ],
    difficulty: 'medium',
    strictRules: {
      allowedTurns: ['straight'],
      forbiddenTurns: ['left', 'right', 'uturn'],
      mustStayInLane: 2,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 7: North Entry, Lane 3 → U-Turn
  {
    id: 'rb_north_lane3_uturn',
    name: 'North Entry - Lane 3 U-Turn',
    description: 'Enter from north in Lane 3 (innermost), perform U-turn around green island back to north',
    entryDirection: 'north',
    entryLane: 3,
    expectedTurn: 'uturn',
    expectedExit: 'north',
    start: { x: 210, y: 30, angle: Math.PI / 2 }, // North road, Lane 3 (innermost)
    checkpoints: [
      { x: 210, y: 90, type: 'approach', rule: 'Approach roundabout in Lane 3 (innermost)' },
      { x: 212, y: 120, type: 'enter', rule: 'Enter roundabout in circulating Lane 3' },
      { x: 214, y: 145, type: 'circulate_1', rule: 'Circulate clockwise around green island (Lane 3)' },
      { x: 200, y: 165, type: 'circulate_2', rule: 'Continue around island - pass Exit 1 (west)' },
      { x: 180, y: 180, type: 'circulate_3', rule: 'Keep circulating - traveling around green island' },
      { x: 165, y: 200, type: 'circulate_4', rule: 'Left side of island - continue around' },
      { x: 165, y: 224, type: 'circulate_5', rule: 'Pass Exit 2 (south) - stay in Lane 3' },
      { x: 180, y: 244, type: 'circulate_6', rule: 'Continue circulating around island' },
      { x: 200, y: 259, type: 'circulate_7', rule: 'Pass Exit 3 (east) - stay in Lane 3' },
      { x: 224, y: 259, type: 'circulate_8', rule: 'Right side of island - continue around' },
      { x: 244, y: 244, type: 'circulate_9', rule: 'Complete circle around green island' },
      { x: 259, y: 224, type: 'circulate_10', rule: 'Approaching Exit 4 (back to north)' },
      { x: 259, y: 200, type: 'prepare_exit', rule: 'Prepare to exit back to north road (U-turn)' },
      { x: 244, y: 180, type: 'exit', rule: 'Exit back to north road (Lane 3)' },
      { x: 224, y: 165, type: 'exit_complete', rule: 'Exiting roundabout' },
      { x: 210, y: 130, type: 'complete', rule: 'U-turn completed - traveled 360° around island' }
    ],
    difficulty: 'hard',
    strictRules: {
      allowedTurns: ['straight', 'right', 'uturn'],
      forbiddenTurns: ['left'],
      mustStayInLane: 3,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 8: East Entry, Lane 3 → Right Turn
  {
    id: 'rb_east_lane3_right',
    name: 'East Entry - Lane 3 Right Turn',
    description: 'Enter from east in Lane 3, take third exit (right turn to north)',
    entryDirection: 'east',
    entryLane: 3,
    expectedTurn: 'right',
    expectedExit: 'north',
    start: { x: 970, y: 270, angle: Math.PI }, // East road, Lane 3
    checkpoints: [
      { x: 900, y: 270, type: 'approach', rule: 'Approach roundabout in Lane 3' },
      { x: 330, y: 270, type: 'entry_prepare', rule: 'Prepare to enter roundabout' },
      { x: 290, y: 255, type: 'enter', rule: 'Enter roundabout in circulating Lane 3' },
      { x: 270, y: 240, type: 'circulate_1', rule: 'Circulate clockwise in Lane 3 around green island' },
      { x: 253, y: 225, type: 'circulate_2', rule: 'Continue around island - pass Exit 1 (south)' },
      { x: 238, y: 205, type: 'circulate_3', rule: 'Keep circulating around green island' },
      { x: 223, y: 190, type: 'circulate_4', rule: 'Pass Exit 2 (west) - stay in Lane 3' },
      { x: 205, y: 175, type: 'circulate_5', rule: 'Continue circulating around island' },
      { x: 190, y: 165, type: 'circulate_6', rule: 'Top of island - approaching Exit 3 (north)' },
      { x: 175, y: 165, type: 'circulate_7', rule: 'Continue around island toward Exit 3' },
      { x: 160, y: 175, type: 'circulate_8', rule: 'Approaching Exit 3 (north) - prepare to exit' },
      { x: 153, y: 190, type: 'prepare_exit', rule: 'Prepare to exit to north road (270° turn)' },
      { x: 165, y: 165, type: 'exit', rule: 'Exit to north road in Lane 3' },
      { x: 180, y: 140, type: 'exit_complete', rule: 'Exiting roundabout to north' },
      { x: 200, y: 100, type: 'complete', rule: 'Right turn completed - traveled 270° around island' }
    ],
    difficulty: 'medium',
    strictRules: {
      allowedTurns: ['straight', 'right'],
      forbiddenTurns: ['left', 'uturn', 'left_270', 'full_circle'],
      mustStayInLane: 3,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 9: South Entry, Lane 2 → Straight Through
  {
    id: 'rb_south_lane2_straight',
    name: 'South Entry - Lane 2 Straight',
    description: 'Enter from south in Lane 2, go straight through to north',
    entryDirection: 'south',
    entryLane: 2,
    expectedTurn: 'straight',
    expectedExit: 'north',
    start: { x: 250, y: 570, angle: -Math.PI / 2 }, // South road, Lane 2
    checkpoints: [
      { x: 250, y: 500, type: 'approach', rule: 'Approach roundabout in Lane 2' },
      { x: 248, y: 310, type: 'entry_prepare', rule: 'Prepare to enter roundabout' },
      { x: 245, y: 290, type: 'enter', rule: 'Enter roundabout in circulating Lane 2' },
      { x: 235, y: 265, type: 'circulate_1', rule: 'Circulate in Lane 2 - pass first exit (east)' },
      { x: 220, y: 235, type: 'circulate_2', rule: 'Continue circulating in Lane 2' },
      { x: 205, y: 210, type: 'circulate_3', rule: 'Approach second exit (straight/north)' },
      { x: 195, y: 185, type: 'prepare_exit', rule: 'Prepare to exit straight' },
      { x: 185, y: 160, type: 'exit', rule: 'Exit to north road' },
      { x: 180, y: 120, type: 'complete', rule: 'Straight through completed' }
    ],
    difficulty: 'medium',
    strictRules: {
      allowedTurns: ['straight'],
      forbiddenTurns: ['left', 'right', 'uturn'],
      mustStayInLane: 2,
      mustNotTouchIsland: true
    }
  },

  // SCENARIO 10: West Entry, Lane 4 → Left 270° Turn (Advanced)
  {
    id: 'rb_west_lane4_left270',
    name: 'West Entry - Lane 4 Left 270° Turn',
    description: 'Enter from west in Lane 4 (innermost), travel 270° around green island to south exit',
    entryDirection: 'west',
    entryLane: 4,
    expectedTurn: 'left_270',
    expectedExit: 'south',
    start: { x: 30, y: 210, angle: 0 }, // West road, Lane 4 (innermost)
    checkpoints: [
      { x: 100, y: 210, type: 'approach', rule: 'Approach roundabout in Lane 4 (innermost)' },
      { x: 125, y: 212, type: 'enter', rule: 'Enter roundabout in circulating Lane 4' },
      { x: 150, y: 212, type: 'circulate_1', rule: 'Circulate clockwise around green island (Lane 4)' },
      { x: 175, y: 200, type: 'circulate_2', rule: 'Continue around island - pass Exit 1 (north)' },
      { x: 195, y: 180, type: 'circulate_3', rule: 'Keep circulating - traveling around green island' },
      { x: 214, y: 165, type: 'circulate_4', rule: 'Top of island - continue around (270° path)' },
      { x: 233, y: 180, type: 'circulate_5', rule: 'Pass Exit 2 (east) - stay in Lane 4' },
      { x: 253, y: 200, type: 'circulate_6', rule: 'Continue circulating 270° around island' },
      { x: 265, y: 212, type: 'circulate_7', rule: 'Approaching Exit 3 (south) - 270° turn complete' },
      { x: 259, y: 224, type: 'prepare_exit', rule: 'Prepare to exit at third exit (270° turn)' },
      { x: 244, y: 244, type: 'exit', rule: 'Exit to south road (270° completed)' },
      { x: 224, y: 259, type: 'exit_complete', rule: 'Exiting roundabout' },
      { x: 210, y: 290, type: 'complete', rule: 'Left 270° turn completed - traveled around island to Exit 3' }
    ],
    difficulty: 'hard',
    strictRules: {
      allowedTurns: ['right', 'uturn', 'left_270', 'full_circle'],
      forbiddenTurns: ['left', 'straight'],
      mustStayInLane: 4,
      mustNotTouchIsland: true
    }
  }
];

// NON-ROUNDABOUT SCENARIOS (kept for variety)
const OTHER_SCENARIOS = [
  {
    id: 'parking_maneuver',
    name: 'Parking Maneuver',
    description: 'Navigate to parking area and park',
    start: { x: 190, y: 80, angle: Math.PI / 2 },
    checkpoints: [
      { x: 190, y: 350, type: 'approach', rule: 'Approach parking access road' },
      { x: 250, y: 380, type: 'entry', rule: 'Turn into parking access' },
      { x: 320, y: 380, type: 'navigate', rule: 'Navigate parking area slowly' },
      { x: 380, y: 360, type: 'park', rule: 'Find parking slot' },
      { x: 420, y: 350, type: 'complete', rule: 'Perfect parking!' }
    ],
    difficulty: 'medium'
  },
  {
    id: 'lane_discipline',
    name: 'Lane Discipline',
    description: 'Maintain correct lane on multi-lane road',
    start: { x: 30, y: 150, angle: 0 },
    checkpoints: [
      { x: 150, y: 150, type: 'maintain', rule: 'Stay in your lane' },
      { x: 350, y: 150, type: 'maintain', rule: 'Do not drift between lanes' },
      { x: 550, y: 150, type: 'maintain', rule: 'Maintain consistent position' },
      { x: 750, y: 150, type: 'maintain', rule: 'Good lane discipline' },
      { x: 950, y: 150, type: 'complete', rule: 'Excellent! Lane discipline maintained' }
    ],
    difficulty: 'easy'
  }
];

// Combine all scenarios
const ROUTE_DEFINITIONS = [...ROUNDABOUT_SCENARIOS, ...OTHER_SCENARIOS];

// Generate a random route for the player
export const generateRoute = () => {
  const randomIndex = Math.floor(Math.random() * ROUTE_DEFINITIONS.length);
  return ROUTE_DEFINITIONS[randomIndex];
};

// Get a specific route by ID
export const getRoute = (routeId) => {
  return ROUTE_DEFINITIONS.find(route => route.id === routeId);
};

// Get all available routes
export const getAllRoutes = () => {
  return ROUTE_DEFINITIONS;
};

// Get only roundabout routes
export const getRoundaboutRoutes = () => {
  return ROUNDABOUT_SCENARIOS;
};

// Validate if vehicle position is on correct path (proximity-based only)
export const validatePosition = (x, y, checkpoint, checkpointIndex, route) => {
  const dx = x - checkpoint.x;
  const dy = y - checkpoint.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  const tolerance = 40;
  return distance <= tolerance;
};

// Check if route is completed
export const checkRouteCompletion = (currentCheckpoint, totalCheckpoints) => {
  return currentCheckpoint >= totalCheckpoints;
};

/**
 * ROUNDABOUT VALIDATION FUNCTIONS
 */

// Determine which direction vehicle is entering from
export const detectEntryDirection = (x, y) => {
  const dx = x - ROUNDABOUT_CENTER.x;
  const dy = y - ROUNDABOUT_CENTER.y;
  const angle = Math.atan2(dy, dx);
  const degrees = angle * (180 / Math.PI);

  if (degrees >= -45 && degrees < 45) return 'east';
  if (degrees >= 45 && degrees < 135) return 'south';
  if (degrees >= -135 && degrees < -45) return 'north';
  return 'west';
};

// Determine which lane the vehicle is in before entering roundabout
export const detectEntryLane = (x, y, entryDirection) => {
  let laneNumber = null;

  switch (entryDirection) {
    case 'north': // Coming from top (vertical road going down)
      // Going DOWN: Use lanes on LEFT side of median (160-218)
      // Lane 1 (OUTERMOST, near kerb): 160-180
      // Lane 2 (middle): 180-200
      // Lane 3 (INNERMOST, near median): 200-218
      if (x >= 160 && x < 180) laneNumber = 1;
      else if (x >= 180 && x < 200) laneNumber = 2;
      else if (x >= 200 && x < 222) laneNumber = 3;
      break;

    case 'south': // Coming from bottom (vertical road going up)
      // Going UP: Use lanes on RIGHT side of median (222-280)
      // Lane 1 (OUTERMOST): 222-240
      // Lane 2: 240-260
      // Lane 3 (INNERMOST): 260-280
      if (x >= 222 && x < 240) laneNumber = 1;
      else if (x >= 240 && x < 260) laneNumber = 2;
      else if (x >= 260 && x <= 280) laneNumber = 3;
      break;

    case 'west': // Coming from left (horizontal road going right)
      // Going RIGHT: Use TOP 4 lanes (140-217)
      // Lane 1 (OUTERMOST): 140-160
      // Lane 2: 160-180
      // Lane 3: 180-200
      // Lane 4 (INNERMOST): 200-217
      if (y >= 140 && y < 160) laneNumber = 1;
      else if (y >= 160 && y < 180) laneNumber = 2;
      else if (y >= 180 && y < 200) laneNumber = 3;
      else if (y >= 200 && y < 223) laneNumber = 4;
      break;

    case 'east': // Coming from right (horizontal road going left)
      // Going LEFT: Use BOTTOM 4 lanes (223-300)
      // Lane 1 (OUTERMOST): 223-243
      // Lane 2: 243-263
      // Lane 3: 263-283
      // Lane 4 (INNERMOST): 283-300
      if (y >= 223 && y < 243) laneNumber = 1;
      else if (y >= 243 && y < 263) laneNumber = 2;
      else if (y >= 263 && y < 283) laneNumber = 3;
      else if (y >= 283 && y <= 300) laneNumber = 4;
      break;
  }

  return laneNumber;
};

// Check if position is near roundabout entry
export const isNearRoundaboutEntry = (x, y) => {
  const dx = x - ROUNDABOUT_CENTER.x;
  const dy = y - ROUNDABOUT_CENTER.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return dist <= 120 && dist >= 80;
};

// Check if position is inside roundabout
export const isInsideRoundabout = (x, y) => {
  const dx = x - ROUNDABOUT_CENTER.x;
  const dy = y - ROUNDABOUT_CENTER.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return dist <= ROUNDABOUT_OUTER_RADIUS;
};

// Check if position is on/in the green island (FORBIDDEN)
export const isTouchingGreenIsland = (x, y) => {
  const dx = x - ROUNDABOUT_CENTER.x;
  const dy = y - ROUNDABOUT_CENTER.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  return dist <= GREEN_ISLAND_RADIUS;
};

// Validate vehicle is in correct lane circle inside roundabout
export const validateRoundaboutLanePosition = (x, y, entryLane) => {
  const dx = x - ROUNDABOUT_CENTER.x;
  const dy = y - ROUNDABOUT_CENTER.y;
  const distFromCenter = Math.sqrt(dx * dx + dy * dy);

  const laneRadii = CIRCULATING_LANE_RADII[entryLane];
  if (!laneRadii) return false;

  const TOLERANCE = 10;
  return distFromCenter >= (laneRadii.min - TOLERANCE) &&
         distFromCenter <= (laneRadii.max + TOLERANCE);
};

// Detect which exit the vehicle is taking (1-4)
export const detectRoundaboutExit = (x, y, entryDirection) => {
  const exitDirection = detectEntryDirection(x, y);

  const directions = ['north', 'east', 'south', 'west'];
  const entryIndex = directions.indexOf(entryDirection);
  const exitIndex = directions.indexOf(exitDirection);

  if (entryIndex === -1 || exitIndex === -1) return null;

  let exitNumber = (exitIndex - entryIndex + 4) % 4;
  if (exitNumber === 0) exitNumber = 4; // Same direction = U-turn (exit 4)

  return exitNumber;
};

// Validate roundabout exit based on entry lane and num lanes
export const validateRoundaboutExit = (entryLane, exitNumber, numLanesAtEntry) => {
  if (numLanesAtEntry === 3) {
    // Three-lane approach
    switch (entryLane) {
      case 1: return exitNumber === 1 || exitNumber === 2; // Lane 1: Left, Straight
      case 2: return exitNumber === 2; // Lane 2: Straight only
      case 3: return exitNumber === 2 || exitNumber === 3 || exitNumber === 4; // Lane 3: Straight, Right, U-Turn
      default: return false;
    }
  } else if (numLanesAtEntry === 4) {
    // Four-lane approach
    switch (entryLane) {
      case 1: return exitNumber === 1 || exitNumber === 2; // Lane 1: Left, Straight
      case 2: return exitNumber === 2; // Lane 2: Straight only
      case 3: return exitNumber === 2 || exitNumber === 3; // Lane 3: Straight, Right
      case 4: return exitNumber === 3 || exitNumber === 4; // Lane 4: Right, U-Turn, 270°, Full Circle
      default: return false;
    }
  }

  return false;
};

// Get the number of lanes for a given entry direction
export const getNumLanesForDirection = (entryDirection) => {
  if (entryDirection === 'north' || entryDirection === 'south') {
    return 3; // Vertical road: 3 lanes per direction
  } else {
    return 4; // Horizontal road: 4 lanes per direction
  }
};

// Generate error message for roundabout violation
export const getRoundaboutErrorMessage = (entryLane, exitNumber, numLanes) => {
  const exitNames = ['', '1st exit (LEFT)', '2nd exit (STRAIGHT)', '3rd exit (RIGHT)', '4th exit (U-TURN)'];

  let allowedExits = '';
  if (numLanes === 3) {
    if (entryLane === 1) allowedExits = '1st exit (LEFT) or 2nd exit (STRAIGHT)';
    else if (entryLane === 2) allowedExits = '2nd exit (STRAIGHT) ONLY';
    else if (entryLane === 3) allowedExits = '2nd exit (STRAIGHT), 3rd exit (RIGHT), or U-TURN';
  } else if (numLanes === 4) {
    if (entryLane === 1) allowedExits = '1st exit (LEFT) or 2nd exit (STRAIGHT)';
    else if (entryLane === 2) allowedExits = '2nd exit (STRAIGHT) ONLY';
    else if (entryLane === 3) allowedExits = '2nd exit (STRAIGHT) or 3rd exit (RIGHT)';
    else if (entryLane === 4) allowedExits = '3rd exit (RIGHT), U-TURN, 270° TURN, or FULL CIRCLE';
  }

  return {
    title: 'ILLEGAL ROUNDABOUT EXIT',
    description: `You entered in Lane ${entryLane} (${entryLane === 1 ? 'OUTERMOST' : entryLane === numLanes ? 'INNERMOST' : 'middle lane'}) but took the ${exitNames[exitNumber]}.`,
    rule: `Lane ${entryLane} permits: ${allowedExits}`,
    reminder: 'KENYA MTB RULE: Each lane permits specific exits. NO LANE CHANGES inside roundabout. Stay in your circulating lane until exit.'
  };
};

/**
 * ROAD TYPE AND LINE CROSSING VALIDATION
 */

export const validateRoadType = (x, y) => {
  const TOLERANCE = 8;

  // Roundabout area - ALWAYS VALID (except green island)
  const dx = x - ROUNDABOUT_CENTER.x;
  const dy = y - ROUNDABOUT_CENTER.y;
  const distFromRoundabout = Math.sqrt(dx * dx + dy * dy);
  if (distFromRoundabout <= (ROUNDABOUT_OUTER_RADIUS + TOLERANCE)) return true;

  // Horizontal main road
  if (y >= (140 - TOLERANCE) && y <= (300 + TOLERANCE)) return true;

  // Vertical main road
  if (x >= (160 - TOLERANCE) && x <= (280 + TOLERANCE) && y <= (520 + TOLERANCE)) return true;

  // Bottom road
  if (y >= (520 - TOLERANCE) && y <= (560 + TOLERANCE)) return true;

  // Drop road
  if (x >= (580 - TOLERANCE) && x <= (620 + TOLERANCE) && y >= (300 - TOLERANCE) && y <= (520 + TOLERANCE)) return true;

  // Parking area and access roads
  if (x >= (280 - TOLERANCE) && x <= (580 + TOLERANCE) && y >= (310 - TOLERANCE) && y <= (450 + TOLERANCE)) return true;

  return false;
};

export const getDifficultyColor = (difficulty) => {
  switch (difficulty) {
    case 'easy': return '#4caf50';
    case 'medium': return '#ff9800';
    case 'hard': return '#f44336';
    default: return '#9e9e9e';
  }
};

export const isOnContinuousYellowLine = (x, y) => {
  const YELLOW_LINE_THICKNESS = 4;

  // Bottom road yellow line
  const bottomRoadCenterY = 540;
  if (Math.abs(y - bottomRoadCenterY) <= YELLOW_LINE_THICKNESS) {
    if (x >= 130 && x <= 310) return false;
    if (x >= 550 && x <= 650) return false;
    return true;
  }

  // Horizontal main road median
  const horizontalMedianY = 220;
  if (Math.abs(y - horizontalMedianY) <= YELLOW_LINE_THICKNESS) {
    if (x >= 114 && x <= 314) return false;
    if (x >= 450 && x <= 510) return false;
    if (x >= 650 && x <= 710) return false;
    return true;
  }

  // Vertical main road median
  const verticalMedianX = 220;
  if (Math.abs(x - verticalMedianX) <= YELLOW_LINE_THICKNESS) {
    if (y >= 112 && y <= 312) return false;
    return true;
  }

  return false;
};

export const didCrossYellowLine = (prevX, prevY, newX, newY) => {
  // Check bottom road yellow line
  const bottomRoadCenterY = 540;
  if ((prevY < bottomRoadCenterY && newY > bottomRoadCenterY) ||
      (prevY > bottomRoadCenterY && newY < bottomRoadCenterY)) {
    const crossingX = prevX + (newX - prevX) * ((bottomRoadCenterY - prevY) / (newY - prevY));
    if (isOnContinuousYellowLine(crossingX, bottomRoadCenterY)) {
      return { crossed: true, type: 'bottom road', position: { x: crossingX, y: bottomRoadCenterY } };
    }
  }

  // Check horizontal median
  const horizontalMedianY = 220;
  if ((prevY < horizontalMedianY && newY > horizontalMedianY) ||
      (prevY > horizontalMedianY && newY < horizontalMedianY)) {
    const crossingX = prevX + (newX - prevX) * ((horizontalMedianY - prevY) / (newY - prevY));
    if (isOnContinuousYellowLine(crossingX, horizontalMedianY)) {
      return { crossed: true, type: 'horizontal median', position: { x: crossingX, y: horizontalMedianY } };
    }
  }

  // Check vertical median
  const verticalMedianX = 220;
  if ((prevX < verticalMedianX && newX > verticalMedianX) ||
      (prevX > verticalMedianX && newX < verticalMedianX)) {
    const crossingY = prevY + (newY - prevY) * ((verticalMedianX - prevX) / (newX - prevX));
    if (isOnContinuousYellowLine(verticalMedianX, crossingY)) {
      return { crossed: true, type: 'vertical median', position: { x: verticalMedianX, y: crossingY } };
    }
  }

  return { crossed: false };
};

/**
 * Check if vehicle crossed a continuous white line at roundabout entry
 * These are the solid white lane markings 30px before the roundabout
 */
export const didCrossRoundaboutEntryLine = (prevX, prevY, newX, newY) => {
  const centerX = ROUNDABOUT_CENTER.x;
  const centerY = ROUNDABOUT_CENTER.y;
  const outerRadius = ROUNDABOUT_OUTER_RADIUS;

  // Solid lines start 30px before roundabout edge
  const westSolidStart = centerX - outerRadius - 30; // x = 84
  const eastSolidStart = centerX + outerRadius + 30; // x = 344
  const northSolidStart = centerY - outerRadius - 30; // y = 82
  const southSolidStart = centerY + outerRadius + 30; // y = 342

  // Define lane widths - 20px each
  const laneWidth = 20;

  // West approach - horizontal white lines between lanes (y positions)
  // 4 lanes: y = 150, 170, 190, 210 (top edge of each lane except lane 1)
  const westLaneLines = [150 + laneWidth, 150 + 2*laneWidth, 150 + 3*laneWidth]; // [170, 190, 210]

  for (const lineY of westLaneLines) {
    // Check if vehicle crossed this horizontal line in the solid section (x < westSolidStart + roundabout width)
    if ((prevY < lineY && newY > lineY) || (prevY > lineY && newY < lineY)) {
      const crossingX = prevX + (newX - prevX) * ((lineY - prevY) / (newY - prevY));
      // Check if crossing happened in the solid white line zone (approaching roundabout from west)
      if (crossingX >= westSolidStart && crossingX <= centerX - outerRadius) {
        return {
          crossed: true,
          type: 'roundabout entry line (west approach)',
          position: { x: crossingX, y: lineY }
        };
      }
    }
  }

  // East approach - horizontal white lines between lanes
  const eastLaneLines = [150 + laneWidth, 150 + 2*laneWidth, 150 + 3*laneWidth]; // [170, 190, 210]

  for (const lineY of eastLaneLines) {
    if ((prevY < lineY && newY > lineY) || (prevY > lineY && newY < lineY)) {
      const crossingX = prevX + (newX - prevX) * ((lineY - prevY) / (newY - prevY));
      // Check if crossing happened in solid zone (approaching roundabout from east)
      if (crossingX <= eastSolidStart && crossingX >= centerX + outerRadius) {
        return {
          crossed: true,
          type: 'roundabout entry line (east approach)',
          position: { x: crossingX, y: lineY }
        };
      }
    }
  }

  // North approach - vertical white lines between lanes (x positions)
  // 3 lanes on vertical road: roadX = 160, so lanes at x = 180, 200
  const northLaneLines = [160 + laneWidth, 160 + 2*laneWidth]; // [180, 200]

  for (const lineX of northLaneLines) {
    if ((prevX < lineX && newX > lineX) || (prevX > lineX && newX < lineX)) {
      const crossingY = prevY + (newY - prevY) * ((lineX - prevX) / (newX - prevX));
      // Check if crossing happened in solid zone (approaching roundabout from north)
      if (crossingY >= northSolidStart && crossingY <= centerY - outerRadius) {
        return {
          crossed: true,
          type: 'roundabout entry line (north approach)',
          position: { x: lineX, y: crossingY }
        };
      }
    }
  }

  // South approach - vertical white lines between lanes (after median)
  // 3 lanes: roadX + 60 = 220, so lanes at x = 240, 260
  const southLaneLines = [220 + laneWidth, 220 + 2*laneWidth]; // [240, 260]

  for (const lineX of southLaneLines) {
    if ((prevX < lineX && newX > lineX) || (prevX > lineX && newX < lineX)) {
      const crossingY = prevY + (newY - prevY) * ((lineX - prevX) / (newX - prevX));
      // Check if crossing happened in solid zone (approaching roundabout from south)
      if (crossingY <= southSolidStart && crossingY >= centerY + outerRadius) {
        return {
          crossed: true,
          type: 'roundabout entry line (south approach)',
          position: { x: lineX, y: crossingY }
        };
      }
    }
  }

  return { crossed: false };
};
